// Single source of truth for design tokens.
// Import these into style objects so palette / spacing changes are global.
// New code MUST use these instead of hardcoded values (see ESLint rule
// mayday/no-style-magic-numbers and the docs at memory/planning/style-system.md).

// ─── Color palette ─────────────────────────────────────────────
//
// Mayday Studio brand kit (Modules v1) calibrated to a Linear-style dark UI.
// Surfaces are warm charcoals derived from Abyss (#231F20): the outer chrome
// (sidebar) is the darkest layer, the content panel sits one step up, and
// cards / popovers step up from there. Text uses rgba(white, …) so alpha
// conveys hierarchy. Brick is the single action accent (primary buttons,
// focus, selection); Tide and Sand are module accents; red-for-live stays
// Brick. White text on a Brick fill passes WCAG AA (5.3:1).
// Tones (success / warning / danger / info) come in bg/border/text triples
// so badges and pills look consistent.

// Raw brand palette — use the semantic tokens below in UI code; reach for
// these only for brand moments (logo tiles, module accents).
export const brand = {
  abyss: '#231F20',
  brick: '#BB4430',
  tide:  '#7EBDC2',
  sand:  '#F3DFA2',
  foam:  '#EFE6DD',
};

export const colors = {
  // Surfaces — warm charcoal layers
  bgChrome:     '#0f0d0e',   // app chrome / sidebar (darkest)
  bg:           '#161314',   // content panel + page background
  bgRaised:     '#1c1819',   // card
  bgHover:      '#231f20',   // hover / cardAlt surface (Abyss)
  bgInput:      'rgba(255,255,255,0.035)',
  bgOverlay:    'rgba(8,6,7,0.62)',
  bgModal:      '#1c1819',
  bgPopover:    '#211d1e',   // menus, tooltips, dropdowns
  bgSelected:   'rgba(255,255,255,0.07)', // neutral selected row (sidebar, drawer, segmented)

  // Borders — hairlines
  border:       'rgba(255,255,255,0.07)',
  borderSubtle: 'rgba(255,255,255,0.05)',
  borderStrong: 'rgba(255,255,255,0.12)',
  borderFocus:  'rgba(187,68,48,0.55)',

  // Text — warm ink on dark, alpha conveys hierarchy
  text:         '#ece7e2',
  textMuted:    'rgba(255,255,255,0.68)',
  textSubtle:   'rgba(255,255,255,0.48)',
  textDim:      'rgba(255,255,255,0.38)',
  textPlaceholder: 'rgba(255,255,255,0.28)',

  // Accent (Brick)
  accent:       '#bb4430',
  accentSoft:   'rgba(187,68,48,0.16)',
  accentFg:     '#e8735e',   // brick readable as text on dark surfaces
  accentBorder: 'rgba(187,68,48,0.42)',
  accentBright: '#d65a43',   // hover-lighten for solid brick fills
  accentDeep:   '#9c3726',   // pressed / hover-darken & categorical swatch
  accentFgSoft: '#f2b3a6',   // pale brick text (badges, subtle links)
  onAccent:     '#ffffff',   // text/icons on a solid brick fill

  // Module accents (brand kit): Tide = research & writing, Sand = production
  tide:         '#7ebdc2',
  tideSoft:     'rgba(126,189,194,0.14)',
  sand:         '#f3dfa2',
  sandSoft:     'rgba(243,223,162,0.14)',
  foam:         '#efe6dd',

  // Brick alpha ramp — same #bb4430 hue at fixed opacities. Use for
  // tints, hairlines, hovers where accentSoft (0.16) isn't the right weight.
  accentA04:    'rgba(187,68,48,0.04)',
  accentA05:    'rgba(187,68,48,0.05)',
  accentA06:    'rgba(187,68,48,0.06)',
  accentA08:    'rgba(187,68,48,0.08)',
  accentA10:    'rgba(187,68,48,0.1)',
  accentA12:    'rgba(187,68,48,0.12)',
  accentA14:    'rgba(187,68,48,0.14)',
  accentA15:    'rgba(187,68,48,0.15)',
  accentA16:    'rgba(187,68,48,0.16)',
  accentA20:    'rgba(187,68,48,0.2)',
  accentA22:    'rgba(187,68,48,0.22)',
  accentA25:    'rgba(187,68,48,0.25)',
  accentA30:    'rgba(187,68,48,0.3)',
  accentA40:    'rgba(187,68,48,0.4)',
  accentA45:    'rgba(187,68,48,0.45)',
  accentA70:    'rgba(187,68,48,0.7)',
  accentA80:    'rgba(187,68,48,0.8)',

  // Neutrals / one-offs
  white:        '#ffffff',   // pure white — text/icons on a colored fill
  textBright:   '#f4efea',   // near-white ink, a touch brighter than `text`
  gold:         '#f3c44f',   // amber accent (stars, highlights)
  whiteA02:     'rgba(255,255,255,0.02)',   // faintest neutral tint (row/strip washes)
  whiteA03:     'rgba(255,255,255,0.03)',
  whiteA05:     'rgba(255,255,255,0.05)',
  whiteA06:     'rgba(255,255,255,0.06)',
  whiteA08:     'rgba(255,255,255,0.08)',
  whiteA45:     'rgba(255,255,255,0.45)',
  blackA30:     'rgba(0,0,0,0.3)',

  // Semantic tones — each has bg / border / fg for badges & pills.
  success: {
    bg:     'rgba(76,183,130,0.14)',
    border: 'rgba(76,183,130,0.32)',
    fg:     '#4cb782',
    fgSoft: '#93d6b0',
  },
  // Emerald — a distinct teal-green cousin of success, used for active/paid
  // states (Deliverables) where a cooler green reads as "settled" not "new".
  emerald: {
    bg:     'rgba(16,185,129,0.15)',
    border: 'rgba(16,185,129,0.35)',
    fg:     '#10b981',
    fgSoft: '#34d399',
  },
  warning: {
    bg:     'rgba(233,185,73,0.14)',
    border: 'rgba(233,185,73,0.32)',
    fg:     '#e9b949',
    fgSoft: '#f3dfa2',
  },
  danger: {
    bg:     'rgba(229,72,77,0.14)',
    border: 'rgba(229,72,77,0.32)',
    fg:     '#e5484d',
    fgSoft: '#f4a3a5',
  },
  info: {
    bg:     'rgba(126,189,194,0.14)',
    border: 'rgba(126,189,194,0.32)',
    fg:     '#7ebdc2',
    fgSoft: '#b5dade',
  },
  // Violet — added for the suite launcher's per-app monogram tints (Drift),
  // where the six existing hues (steel, sky, green, teal, amber, red) run
  // out. General-purpose tone triple, same shape as the others.
  violet: {
    bg:     'rgba(139,92,246,0.15)',
    border: 'rgba(139,92,246,0.35)',
    fg:     '#8b5cf6',
    fgSoft: '#c4b5fd',
  },
  // Pink — added for the suite launcher's Gerald (Mayday Assistant) monogram
  // tint; the seven existing hues were already claimed by suite apps.
  pink: {
    bg:     'rgba(236,72,153,0.15)',
    border: 'rgba(236,72,153,0.35)',
    fg:     '#ec4899',
    fgSoft: '#f9a8d4',
  },

  // ── Pipeline node states ──────────────────────────────────────
  //
  // The Pipeline view spends its colour budget entirely on state, so these
  // two fill the gaps the semantic tones above don't cover.
  //
  // `locked` is intentionally NOT a hue. A locked node means "upstream isn't
  // done yet", which is an absence of state rather than a condition — so it
  // reads as a recessed surface with dim ink, and recedes behind every
  // coloured node next to it. Contrast is deliberately below body-text
  // minimums: locked nodes carry no information you need to read, and their
  // stage name repeats on the nodes above.
  locked: {
    bg:     'rgba(255,255,255,0.035)',
    border: 'rgba(255,255,255,0.08)',
    fg:     'rgba(255,255,255,0.3)',
    fgSoft: 'rgba(255,255,255,0.22)',
  },
  // `blocked` is danger's hue, named separately so pipeline code says what it
  // means. Keep the values in sync with `danger` — if the danger tone moves,
  // this moves with it.
  blocked: {
    bg:     'rgba(229,72,77,0.14)',
    border: 'rgba(229,72,77,0.32)',
    fg:     '#e5484d',
    fgSoft: '#f4a3a5',
  },
};

// ─── Spacing scale ─────────────────────────────────────────────
//
// 4-px base. Use these for padding, margin, gap. Do not pick 5, 7, 9,
// etc. — if the design genuinely needs an off-scale value, add it here
// with a reason in the doc.

export const spacing = {
  xs:  4,
  sm:  8,
  md:  12,
  lg:  16,
  xl:  20,
  xxl: 24,
  xxxl: 32,
  huge: 48,
};

// ─── Border radii ──────────────────────────────────────────────

export const radii = {
  xs: 4,
  sm: 6,
  md: 6,
  lg: 8,
  xl: 12,
  pill: 999,
  circle: '50%',
};

// ─── Type ramp ────────────────────────────────────────────────

export const fontSizes = {
  xxs: 10,   // tiny labels, dot indicators
  xs:  11,   // section labels, metadata
  sm:  12,   // secondary text
  md:  13,   // body default
  lg:  14,   // emphasized body, card titles
  xl:  16,   // page subsections
  xxl: 18,   // page titles
  display: 22,
  displayLg: 28,  // hero headlines (suite launcher, coming-soon pages); pairs with mobileTokens.font.title
};

export const fontWeights = {
  regular: 400,
  medium:  500,
  semibold: 600,
  bold:    700,
};

// Live (Harbor) app — Apple TV–style surfaces: a near-black page, frosted
// white-alpha glass, and display sizes for the show hero. Used by
// src/pages/harbor/ui/TvKit.js and the show page / Studio sheets.
export const live = {
  pageBg: '#0b0a0a',
  barBg: 'rgba(11,10,10,0.6)',           // sticky top bar over the page
  scrim: 'rgba(6,5,5,0.72)',
  glass02: 'rgba(255,255,255,0.02)',
  glass03: 'rgba(255,255,255,0.03)',
  glass04: 'rgba(255,255,255,0.04)',
  glass05: 'rgba(255,255,255,0.05)',
  glass07: 'rgba(255,255,255,0.07)',
  glass08: 'rgba(255,255,255,0.08)',
  glass16: 'rgba(255,255,255,0.16)',
  glass35: 'rgba(255,255,255,0.35)',
  glass85: 'rgba(255,255,255,0.85)',
  textOnArt: 'rgba(255,255,255,0.78)',  // meta text over hero artwork
  textOnArtSoft: 'rgba(255,255,255,0.7)',
  textOnArtDim: 'rgba(255,255,255,0.5)',
  badgeBg: 'rgba(0,0,0,0.55)',         // chips over artwork
  hairline: 2,                          // segmented-control inset, switch knob
  heroTitle: 56,
  heroTitleSm: 44,
  sheetTitle: 34,
  glyphLg: 44,
  glyph: 24,
  radiusCard: 16,                       // big preview frames
  radiusPanel: 20,                      // empty-state drop zones
};

export const fontFamily = '"Inter", system-ui, -apple-system, sans-serif';

// Monospace — file paths, IDs, code-ish values (e.g. Harbor NAS paths).
export const fontFamilyMono = '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace';

// ─── Shadows ──────────────────────────────────────────────────

export const shadows = {
  sm:    '0 1px 2px rgba(0,0,0,0.3)',
  md:    '0 1px 2px rgba(0,0,0,0.25)',
  lg:    '0 8px 24px rgba(0,0,0,0.45)',
  modal: '0 16px 48px rgba(0,0,0,0.55)',
  inset: '0 1px 0 rgba(255,255,255,0.03) inset',
};

// ─── Focus ring (a11y) ────────────────────────────────────────
//
// Applied globally via `*:focus-visible` in App.js. The `!important`
// there overrides the many inline `outline:'none'` declarations so
// keyboard users always get a visible ring, while mouse users don't
// (that's what :focus-visible buys us). `shadow` is for custom
// widgets that need an inline ring on a non-focusable wrapper.

export const focusRing = {
  color:   'rgba(232,115,94,0.9)',
  outline: '2px solid rgba(232,115,94,0.9)',
  offset:  '2px',
  shadow:  '0 0 0 2px rgba(232,115,94,0.55)',
};

// ─── Motion ───────────────────────────────────────────────────

export const transitions = {
  fast:   'all 80ms ease-out',
  normal: 'all 140ms ease-out',
  slow:   'all 240ms ease-out',
};

// ─── Z-index ──────────────────────────────────────────────────

export const zIndex = {
  base:    1,
  dropdown: 100,
  popover:  200,
  toast:    900,
  modal:    1000,
};

// ─── Re-export everything as a single token bag for ergonomics ─

const tokens = {
  brand,
  colors,
  spacing,
  radii,
  fontSizes,
  fontWeights,
  fontFamily,
  shadows,
  focusRing,
  transitions,
  zIndex,
  live,
};

export default tokens;
